import {
  cn,
  secondsInDay,
  secondsInHour,
  secondsInMinute,
  secondsInYear,
} from "@theprelude/core";
import { FocusEventHandler, forwardRef, useState } from "react";
import type { Entries } from "type-fest";
import { Input } from "../forms/input";
import { Select } from "../forms/select";

export type DurationUnit = "seconds" | "minutes" | "hours" | "days" | "years";

type Duration = { value: number; unit: DurationUnit };
type DurationOption = { id: DurationUnit; label: string };

const defaultDurationOptions: DurationOption[] = [
  { id: "seconds", label: "Seconds" },
  { id: "minutes", label: "Minutes" },
  { id: "hours", label: "Hours" },
  { id: "days", label: "Days" },
  { id: "years", label: "Years" },
];

const defaultDurationUnits = defaultDurationOptions.map((option) => option.id);

// This object must maintain an order of highest to lowest unit
// conversion for this component to behave correctly.
const durationConversion: Record<DurationUnit, number> = {
  years: secondsInYear,
  days: secondsInDay,
  hours: secondsInHour,
  minutes: secondsInMinute,
  seconds: 1,
};

export interface DurationInputProps {
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  value: number | undefined;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLSelectElement>;
  onChange: (value: number | undefined) => void;
  defaultUnit?: DurationUnit;
  allowedUnits?: DurationUnit[];
  className?: string;
  placeholder?: string;
}

export const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
  function DurationInput(
    {
      id,
      disabled,
      required,
      value,
      onBlur,
      onChange,
      defaultUnit,
      allowedUnits = defaultDurationUnits,
      className,
      readOnly,
      placeholder,
    },
    ref,
  ) {
    const [unit, setUnit] = useState<DurationUnit>(() => {
      const idealUnit =
        defaultUnit ??
        (value === undefined ? "seconds" : secondsToDuration(value).unit);
      return allowedUnits.find((u) => u === idealUnit) ?? "seconds";
    });

    const handleChange = (
      newValue: number | undefined,
      newUnit: DurationUnit,
    ) => {
      setUnit(newUnit);
      onChange(
        newValue === undefined
          ? undefined
          : Math.round(newValue * durationConversion[newUnit]),
      );
    };

    const durationOptions = defaultDurationOptions.filter(({ id }) =>
      allowedUnits.includes(id),
    );

    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Input
          ref={ref}
          id={id}
          type="number"
          min="0"
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          value={value === undefined ? "" : value / durationConversion[unit]}
          onBlur={onBlur}
          onChange={(e) =>
            handleChange(
              e.target.value ? Number(e.target.value) : undefined,
              unit,
            )
          }
        />
        <Select
          disabled={disabled || allowedUnits.length === 1}
          readOnly={readOnly}
          required={required}
          value={unit}
          onBlur={onBlur}
          onChange={(e) =>
            handleChange(
              value === undefined
                ? undefined
                : value / durationConversion[unit],
              e.target.value as DurationUnit,
            )
          }
        >
          {durationOptions.map(({ id, label }) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </Select>
      </div>
    );
  },
);

const secondsToDuration = (rawSeconds: number): Duration => {
  const seconds = Math.round(rawSeconds);
  const duration = (
    Object.entries(durationConversion) as Entries<typeof durationConversion>
  ).find(([, conversion]) => seconds % conversion === 0);

  if (!duration) {
    return { value: seconds, unit: "seconds" };
  }

  const [unit, value] = duration;
  return { value, unit };
};
